import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { NumericFormat } from 'react-number-format';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { levelActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { InformModal } from '../../common/SharedComponents';
import { BfiCustomStore, hasPermission, RefreshEvents } from '@apply/ui';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LevelOperatorTypeOptions } from '../common/constants/LevelOperatorTypeOptions';
import { LevelChangeHistoryGrid } from '../common/components/LevelChangeHistoryGrid';
import { LevelChangeReasonModal } from './LevelChangeReasonModal';
import { PermissionType } from '@apply/constants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
    parent: {
        position: 'relative',
        weight: '750px',
    },
    cover: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'block',
        zIndex: '999',
        textAlign: 'center',
        cursor: 'wait',
    },
    messageBox: {
        backgroundColor: 'white',
        display: 'inline-block',
        width: '65%',
        height: '100px',
        marginTop: '200px',
        padding: '20px',
        borderRadius: '10px',
        cursor: 'wait',
    },
    addButton: { height: '38px', float: 'left', marginLeft: '5px' },
    sectionHeader: { margin: '5px 10px' },
};

const LevelModal = () => {
    const { isOpen, levelId, onSave } = useSelector((state) => state.levels.levelModal);
    const { dispatch } = store;
    const [level, set_level] = useState(null);

    const [levelName, set_levelName] = useState('');
    const [soldHomesOperatorId, set_soldHomesOperatorId] = useState('');
    const [soldHomes, set_soldHomes] = useState('');
    const [bfiRepaymentsOperatorId, set_bfiRepaymentsOperatorId] = useState('');
    const [bfiRepayments, set_bfiRepayments] = useState('');
    const [maxLTC, set_maxLTC] = useState('');
    const [maxLTV, set_maxLTV] = useState('');
    const [notes, set_notes] = useState('');

    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [openNamesNotUnique, set_openNamesNotUnique] = useState(false);
    const [changesModalOpen, set_changesModalOpen] = useState(false);

    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const historyStore = new BfiCustomStore();

    const canEdit = hasPermission(PermissionType.ExperienceEditor);

    const _handleClose = () => dispatch(levelActions.setLevelModal({ levelModal: { isOpen: false } }));

    const _validateSubmission = async (e) => {
        e.preventDefault();

        if (!canEdit) {
            handleAdminWarning('You do not have rights to edit this information');
            return;
        }

        const hasChanges = hasOutstandingLevelChanges();
        if (hasChanges) {
            //Display modal and allow user to enters reason for change
            set_changesModalOpen(true);
            return;
        }
    };

    const hasOutstandingLevelChanges = () => {
        let hasChanges = false;

        hasChanges =
            level.levelName !== levelName ||
            level.soldHomesOperatorId !== soldHomesOperatorId ||
            level.soldHomes !== soldHomes ||
            level.bfiRepaymentsOperatorId !== bfiRepaymentsOperatorId ||
            level.bfiRepayments !== bfiRepayments ||
            level.maxLTC !== maxLTC ||
            level.maxLTV !== maxLTV ||
            (level.notes ?? '') !== notes;

        if (hasChanges) {
            return true;
        }

        return hasChanges;
    };

    const _saveLevel = async (changeModel) => {
        try {
            if (!canEdit) {
                handleAdminWarning('You do not have rights to edit this information');
                return;
            }

            set_loadingMessage('Saving...');

            //Ensure names are unique...
            var name = {
                levelName,
                ignoreId: levelId,
            };

            const nameResponse = await experienceApi.validateLevelName(name);
            if (nameResponse.statusCode !== 200) {
                return;
            }

            if (!nameResponse.result) {
                set_openNamesNotUnique(true);
                return;
            }

            const model = {
                id: levelId,
                levelName,
                soldHomesOperatorId,
                soldHomes,
                bfiRepaymentsOperatorId,
                bfiRepayments,
                maxLTC,
                maxLTV,
                notes,
                changeSummary: changeModel,
            };

            const response = await experienceApi.addUpdateLevel(model);
            if (response.statusCode !== 200) {
                return;
            }

            const savedLevel = response.result;
            if (savedLevel.id <= 0) {
                throw new Error('Failed to save new Level!');
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Level successfully saved.');

            if (onSave) {
                onSave(savedLevel.id);
            }

            setTimeout(() => {
                dispatch(
                    levelActions.setLevelModal({
                        levelModal: { isOpen: false },
                    })
                );
            }, 800);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        async function isOpenChanged() {
            if (!isOpen) {
                return;
            }

            try {
                set_loadingMessage('Loading...');

                let level = null;
                if (levelId && levelId > 0) {
                    const response = await experienceApi.getLevel(levelId);
                    if (response.statusCode !== 200) {
                        return;
                    }
                    level = response.result;
                }

                if (!level) {
                    level = {
                        levelName: '',
                        soldHomesOperatorId: '',
                        soldHomes: '',
                        bfiRepaymentsOperatorId: '',
                        bfiRepayments: '',
                        maxLTC: '',
                        maxLTV: '',
                        notes: '',
                    };
                }

                const clone = JSON.parse(JSON.stringify(level));
                set_level(clone);

                set_levelName(level.levelName ?? '');
                set_soldHomesOperatorId(level.soldHomesOperatorId ?? '');
                set_soldHomes(level.soldHomes ?? '');
                set_bfiRepaymentsOperatorId(level.bfiRepaymentsOperatorId ?? '');
                set_bfiRepayments(level.bfiRepayments ?? '');
                set_maxLTC(level.maxLTC ?? '');
                set_maxLTV(level.maxLTV ?? '');
                set_notes(level.notes ?? '');
            } catch (ex) {
                handleAdminError(ex, 'Failed to load Level information. PLease try again');
            } finally {
                set_loadingMessage('');
            }
        }

        isOpenChanged();
    }, [isOpen, levelId]);

    const onChangesModalCancel = () => {
        set_changesModalOpen(false);
    };

    const onChangesModalSubmit = async (model) => {
        set_changesModalOpen(false);
        await _saveLevel(model);
    };

    return (
        <Dialog
            maxWidth='sm'
            open={isOpen}
            onClose={_handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '600px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div style={styles.parent}>
                                <form
                                    name='levelForm'
                                    onSubmit={(event) => _validateSubmission(event)}
                                >
                                    <div className='scrollable-content'>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1>Experience Level</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='levelName'>
                                                        Level Name
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='levelName'
                                                        type='text'
                                                        name='levelName'
                                                        className='req'
                                                        required
                                                        value={levelName}
                                                        onChange={(e) => set_levelName(e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-8'>
                                                    <label htmlFor='soldHomesOperatorType'>
                                                        Sold Homes Operator
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='soldHomesOperatorType'
                                                        value={soldHomesOperatorId}
                                                        onChange={(event) => set_soldHomesOperatorId(event.target.value)}
                                                        disabled={!canEdit}
                                                        required
                                                    >
                                                        <option value=''>Select</option>
                                                        {LevelOperatorTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='soldHomes'>
                                                        Sold Homes
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='soldHomes'
                                                        type='text'
                                                        name='soldHomes'
                                                        required
                                                        value={soldHomes}
                                                        onChange={(e) => set_soldHomes(e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-8'>
                                                    <label htmlFor='bfiRepaymentsOperatorType'>
                                                        BFI Repayments Operator
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='bfiRepaymentsOperatorType'
                                                        value={bfiRepaymentsOperatorId}
                                                        onChange={(event) => set_bfiRepaymentsOperatorId(event.target.value)}
                                                        disabled={!canEdit}
                                                        required
                                                    >
                                                        <option value=''>Select</option>
                                                        {LevelOperatorTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='bfiRepayments'>
                                                        BFI Repayments
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='bfiRepayments'
                                                        type='text'
                                                        name='bfiRepayments'
                                                        required
                                                        value={bfiRepayments}
                                                        onChange={(e) => set_bfiRepayments(e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='maxLTC'>
                                                        Maximum LTC
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='maxLTC'
                                                        onValueChange={(values) => {
                                                            set_maxLTC(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        suffix={'%'}
                                                        placeholder='%'
                                                        value={maxLTC}
                                                        required
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='maximumLTV'>
                                                        Maximum LTV
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='maxLTV'
                                                        onValueChange={(values) => {
                                                            set_maxLTV(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        suffix={'%'}
                                                        placeholder='%'
                                                        value={maxLTV}
                                                        required
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 medium-6 large-12'>
                                                    <label htmlFor='notes'>Notes</label>
                                                    <input
                                                        id='notes'
                                                        type='text'
                                                        name='notes'
                                                        value={notes}
                                                        onChange={(e) => set_notes(e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    padding: '0px 10px 0 10px',
                                                }}
                                            >
                                                <LevelChangeHistoryGrid
                                                    levelId={levelId}
                                                    gridStore={historyStore}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            disabled={!canEdit}
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                                <LevelChangeReasonModal
                                    isOpen={changesModalOpen}
                                    onCancel={onChangesModalCancel}
                                    onSubmit={onChangesModalSubmit}
                                />
                                <InformModal
                                    isOpen={openNamesNotUnique}
                                    title='Duplicate Level!'
                                    text='The Level name that you have entered already exists in the database.'
                                    confirmCallback={() => set_openNamesNotUnique(false)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </Dialog>
    );
};

export default LevelModal;
